import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { ReactComponent as St5Logo } from '../assets/st5.svg';
import { ReactComponent as St5LogoWhite } from '../assets/st5_white.svg';
import engineerImage from '../assets/engineer.jpg';

const STORAGE_KEY = 'st5-styleguide-unlocked';
const PASSWORD = 'st-5';

type ColorToken = {
  name: string;
  token: string;
  hex: string;
  rgb: string;
  role: string;
  textOn?: 'white' | 'navy';
};

const COLORS: ColorToken[] = [
  {
    name: 'Main · Navy',
    token: '--st5-primary',
    hex: '#102F60',
    rgb: '16, 47, 96',
    role: 'Primärfarbe für alles: Navigation, Headlines, Buttons, Links, Icons, Akzente.',
    textOn: 'white',
  },
  {
    name: 'Mensch · Coral',
    token: '--st5-coral',
    hex: '#F37B6E',
    rgb: '243, 123, 110',
    role: 'Sub-Projekt „Mensch" — nur für inhaltliche Zuordnung, nicht für allgemeine UI.',
    textOn: 'white',
  },
  {
    name: 'Resilienz · Purple',
    token: '--st5-purple',
    hex: '#A773B6',
    rgb: '167, 115, 182',
    role: 'Sub-Projekt „Resilienz" — nur für inhaltliche Zuordnung, nicht für allgemeine UI.',
    textOn: 'white',
  },
  {
    name: 'Nachhaltigkeit · Teal',
    token: '--st5-teal',
    hex: '#188580',
    rgb: '24, 133, 128',
    role: 'Sub-Projekt „Nachhaltigkeit" — nur für inhaltliche Zuordnung, nicht für allgemeine UI.',
    textOn: 'white',
  },
  {
    name: 'Gray',
    token: '--st5-gray',
    hex: '#B2B2B2',
    rgb: '178, 178, 178',
    role: 'Neutral — Trennlinien, Rahmen, sekundäre UI.',
    textOn: 'white',
  },
  {
    name: 'Black',
    token: '--st5-black',
    hex: '#000000',
    rgb: '0, 0, 0',
    role: 'Primärer Fließtext.',
    textOn: 'white',
  },
];

const FONT_WEIGHTS = [
  { label: 'Light', weight: 300, style: 'normal' as const },
  { label: 'Regular', weight: 400, style: 'normal' as const },
  { label: 'Regular Italic', weight: 400, style: 'italic' as const },
  { label: 'SemiBold', weight: 600, style: 'normal' as const },
  { label: 'Bold', weight: 700, style: 'normal' as const },
];

type PartnerLogo = {
  name: string;
  role: string;
  file: string;
  url?: string;
};

const PARTNERS: PartnerLogo[] = [
  {
    name: 'IMA Innovationsmanufaktur Gütersloh GmbH',
    role: 'Projektleitung',
    file: '/logos/ima.svg',
  },
  {
    name: 'Hochschule Bielefeld (HSBI)',
    role: 'Wissenschaftspartner',
    file: '/logos/hsbi.svg',
  },
  {
    name: 'KogniHome e.V.',
    role: 'Transferpartner',
    file: '/logos/kognihome.svg',
  },
  {
    name: 'MINTkitchen KG',
    role: 'Transferpartner',
    file: '/logos/mintkitchen.svg',
  },
];

function StyleguideGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (input.trim() === PASSWORD) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="styleguide-gate">
      <form className="styleguide-gate-card" onSubmit={handleSubmit} noValidate>
        <div className="styleguide-gate-logo" aria-hidden="true">
          <St5Logo />
        </div>
        <h1>Interner Styleguide</h1>
        <p className="styleguide-gate-hint">
          Diese Seite ist nur für das Smart Transform 5.0 Team. Bitte Passwort eingeben.
        </p>
        <label className="styleguide-gate-label" htmlFor="sg-password">
          Passwort
        </label>
        <input
          id="sg-password"
          type="password"
          autoComplete="current-password"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError(false);
          }}
          className={`styleguide-gate-input${error ? ' is-error' : ''}`}
          autoFocus
          aria-invalid={error}
          aria-describedby={error ? 'sg-error' : undefined}
        />
        {error && (
          <p id="sg-error" className="styleguide-gate-error" role="alert">
            Passwort ist nicht korrekt.
          </p>
        )}
        <button type="submit" className="styleguide-gate-submit">
          Zugang freischalten
        </button>
      </form>
    </div>
  );
}

function ColorCard({ color }: { color: ColorToken }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(color.hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <article className="sg-color-card">
      <div
        className="sg-color-swatch"
        style={{ backgroundColor: color.hex, color: color.textOn === 'navy' ? '#102F60' : '#ffffff' }}
      >
        <span className="sg-color-swatch-name">{color.name}</span>
      </div>
      <div className="sg-color-body">
        <button
          type="button"
          className="sg-color-hex"
          onClick={handleCopy}
          aria-label={`Hex-Code ${color.hex} kopieren`}
          title="Klicken zum Kopieren"
        >
          {copied ? 'Kopiert!' : color.hex}
        </button>
        <dl className="sg-color-meta">
          <div>
            <dt>RGB</dt>
            <dd>{color.rgb}</dd>
          </div>
          <div>
            <dt>CSS-Token</dt>
            <dd>
              <code>{color.token}</code>
            </dd>
          </div>
        </dl>
        <p className="sg-color-role">{color.role}</p>
      </div>
    </article>
  );
}

type HeroStep =
  | 'image'
  | 'overlay'
  | 'logo'
  | 'text'
  | 'buttons'
  | 'trinity'
  | 'final'
  | null;

const HERO_STEPS: { id: Exclude<HeroStep, null>; label: string }[] = [
  { id: 'image', label: 'Bild' },
  { id: 'overlay', label: '+ Overlay' },
  { id: 'logo', label: '+ Logo' },
  { id: 'text', label: '+ Text' },
  { id: 'buttons', label: '+ Buttons' },
  { id: 'trinity', label: '+ Dreifaltigkeit' },
  { id: 'final', label: 'Ergebnis' },
];

type HeroVariant = 'mensch' | 'resilienz' | 'nachhaltigkeit';

function HeroStage({
  variant,
  tagsAriaLabel,
  children,
}: {
  variant?: HeroVariant;
  tagsAriaLabel: string;
  children: ReactNode;
}) {
  const [heroStep, setHeroStep] = useState<HeroStep>(null);
  const [introRunning, setIntroRunning] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const hasAnimatedRef = useRef(false);
  const introRunningRef = useRef(false);
  const introTimerRef = useRef<number | null>(null);

  const activeIndex = heroStep
    ? HERO_STEPS.findIndex((s) => s.id === heroStep)
    : -1;

  const clearIntroTimer = () => {
    if (introTimerRef.current !== null) {
      window.clearTimeout(introTimerRef.current);
      introTimerRef.current = null;
    }
  };

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const finishIntro = () => {
      introRunningRef.current = false;
      setIntroRunning(false);
      setHeroStep(null);
    };

    const runIntro = () => {
      const stepDuration = 260;
      introRunningRef.current = true;
      setIntroRunning(true);
      let i = 0;
      const tick = () => {
        if (i < HERO_STEPS.length) {
          setHeroStep(HERO_STEPS[i].id);
          i += 1;
          introTimerRef.current = window.setTimeout(tick, stepDuration);
        } else {
          introTimerRef.current = window.setTimeout(finishIntro, stepDuration);
        }
      };
      introTimerRef.current = window.setTimeout(tick, 150);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            runIntro();
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      clearIntroTimer();
    };
  }, []);

  const stageProps: Record<string, string> = {};
  if (variant) stageProps['data-variant'] = variant;

  return (
    <div
      ref={stageRef}
      className="sg-hero-stage"
      data-step={heroStep ?? 'all'}
      data-intro={introRunning ? 'true' : undefined}
      onMouseLeave={() => {
        if (introRunningRef.current) return;
        setHeroStep(null);
      }}
      {...stageProps}
    >
      <div className="sg-hero-tags" role="group" aria-label={tagsAriaLabel}>
        {HERO_STEPS.map((step, index) => (
          <div
            key={step.id}
            className="sg-hero-tag"
            data-active={heroStep === step.id}
            data-reached={activeIndex >= 0 && index <= activeIndex}
            onMouseEnter={() => {
              if (introRunningRef.current) return;
              setHeroStep(step.id);
            }}
          >
            <span className="sg-hero-tag-index" aria-hidden="true">
              {index + 1}
            </span>
            <span className="sg-hero-tag-label">{step.label}</span>
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

function StyleguideContent() {
  return (
    <div className="styleguide">
      <header className="styleguide-header">
        <div className="styleguide-header-inner">
          <p className="styleguide-eyebrow">Internes Dokument · Smart Transform 5.0</p>
          <h1>Styleguide</h1>
          <p className="styleguide-lead">
            Marke, Farben, Typografie, Logos und Partnerlogos des Projekts. Diese Seite ist die
            gemeinsame Referenz für alle Outputs des Teams — Web, Präsentationen, Print.
          </p>
        </div>
      </header>

      <section className="sg-section" aria-labelledby="sg-colors-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-colors-heading">Farben</h2>
            <p>
              Navy ist die Hauptfarbe für alle Standard-UI. Die drei Sub-Projekte werden eindeutig
              über Farbe und Name adressiert: <strong>Mensch</strong> (Coral),{' '}
              <strong>Resilienz</strong> (Purple) und <strong>Nachhaltigkeit</strong> (Teal). Diese
              Farben sind <strong>ausschließlich</strong> für die inhaltliche Zuordnung zum jeweiligen
              Sub-Projekt reserviert — nicht für Buttons, Erfolgsmeldungen oder Dekoration.
            </p>
          </div>
          <div className="sg-color-grid">
            {COLORS.map((c) => (
              <ColorCard key={c.token} color={c} />
            ))}
          </div>
        </div>
      </section>

      <section className="sg-section sg-section--alt" aria-labelledby="sg-logos-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-logos-heading">Logo</h2>
            <p>
              Auf hellen Hintergründen das Navy-Logo verwenden, auf dunklen Hintergründen die
              weiße Variante. Keine anderen Farben, keine Effekte, ausreichend Freiraum lassen.
            </p>
          </div>

          <div className="sg-logo-grid">
            <div className="sg-logo-card sg-logo-card--light">
              <div className="sg-logo-preview">
                <St5Logo />
              </div>
              <div className="sg-logo-meta">
                <span className="sg-logo-title">st5</span>
                <span className="sg-logo-role">Für helle Hintergründe</span>
                <div className="sg-logo-downloads">
                  <a
                    href="/logos/st5.svg"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">SVG</span>
                    <span className="sg-logo-download-hint">Web · skalierbar</span>
                  </a>
                  <a
                    href="/logos/st5.png"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">PNG</span>
                    <span className="sg-logo-download-hint">Office · transparent</span>
                  </a>
                  <a
                    href="/logos/st5.eps"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">EPS</span>
                    <span className="sg-logo-download-hint">Print · Vektor</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="sg-logo-card sg-logo-card--dark">
              <div className="sg-logo-preview">
                <St5LogoWhite />
              </div>
              <div className="sg-logo-meta">
                <span className="sg-logo-title">st5_white</span>
                <span className="sg-logo-role">Für dunkle Hintergründe</span>
                <div className="sg-logo-downloads">
                  <a
                    href="/logos/st5_white.svg"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">SVG</span>
                    <span className="sg-logo-download-hint">Web · skalierbar</span>
                  </a>
                  <a
                    href="/logos/st5_white.png"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">PNG</span>
                    <span className="sg-logo-download-hint">Office · transparent</span>
                  </a>
                  <a
                    href="/logos/st5_white.eps"
                    download
                    className="sg-logo-download"
                  >
                    <span className="sg-logo-download-format">EPS</span>
                    <span className="sg-logo-download-hint">Print · Vektor</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-type-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-type-heading">Typografie</h2>
            <p>
              Die Marken-Schrift ist <strong>Boston</strong>. Sie wird sowohl für Fließtext als auch
              für Headlines verwendet.
            </p>
          </div>

          <div className="sg-font-download-card">
            <div className="sg-font-download-preview" aria-hidden="true">
              <span className="sg-font-download-sample" style={{ fontWeight: 700 }}>
                Aa
              </span>
              <span className="sg-font-download-sample" style={{ fontWeight: 400 }}>
                Aa
              </span>
              <span
                className="sg-font-download-sample"
                style={{ fontWeight: 400, fontStyle: 'italic' }}
              >
                Aa
              </span>
            </div>
            <div className="sg-font-download-body">
              <span className="sg-font-download-title">Boston</span>
              <span className="sg-font-download-role">
                Komplette Familie · 8 Schnitte inkl. Kursiv · 16 OTF-Dateien
              </span>
              <a
                href="/fonts/Boston.zip"
                download
                className="sg-logo-download sg-font-download-button"
              >
                <span className="sg-logo-download-format">ZIP</span>
                <span className="sg-logo-download-hint">Alle Schnitte · ~870 KB</span>
              </a>
            </div>
          </div>

          <div className="sg-type-scale" aria-label="Typografie-Scale">
            <div className="sg-type-row">
              <span className="sg-type-label">H1 · 2.25em · 700</span>
              <span className="sg-type-sample" style={{ fontSize: '2.25em', fontWeight: 700 }}>
                Smart Transform 5.0
              </span>
            </div>
            <div className="sg-type-row">
              <span className="sg-type-label">H2 · 1.75em · 700</span>
              <span className="sg-type-sample" style={{ fontSize: '1.75em', fontWeight: 700 }}>
                Co-creative Transformation
              </span>
            </div>
            <div className="sg-type-row">
              <span className="sg-type-label">H3 · 1.35em · 600</span>
              <span className="sg-type-sample" style={{ fontSize: '1.35em', fontWeight: 600 }}>
                Reagieren Sie noch oder gestalten Sie schon?
              </span>
            </div>
            <div className="sg-type-row">
              <span className="sg-type-label">Body · 1em · 400</span>
              <span className="sg-type-sample" style={{ fontSize: '1em', fontWeight: 400 }}>
                Smart Transform 5.0 unterstützt KMU bei der Transformation hin zur Industrie 5.0.
              </span>
            </div>
            <div className="sg-type-row">
              <span className="sg-type-label">Caption · 0.85em · 400</span>
              <span className="sg-type-sample" style={{ fontSize: '0.85em', fontWeight: 400 }}>
                Gefördert durch EFRE/JTF-Programm NRW 2021–2027.
              </span>
            </div>
          </div>

          <div className="sg-type-weights">
            <h3 className="sg-subheading">Installierte Schnitte</h3>
            <div className="sg-type-weights-grid">
              {FONT_WEIGHTS.map((w) => (
                <div key={`${w.weight}-${w.style}`} className="sg-type-weight">
                  <span className="sg-type-weight-label">
                    {w.label} · {w.weight}
                    {w.style === 'italic' ? ' italic' : ''}
                  </span>
                  <span
                    className="sg-type-weight-sample"
                    style={{ fontWeight: w.weight, fontStyle: w.style }}
                  >
                    The quick brown fox — Ostwestfalen-Lippe 2026
                  </span>
                </div>
              ))}
            </div>
            <p className="sg-type-note">
              Boston hat keinen Medium-Schnitt (500). <code>font-weight: 500</code> fällt auf
              Regular zurück.
            </p>
          </div>
        </div>
      </section>

      <section className="sg-section sg-section--alt" aria-labelledby="sg-buttons-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-buttons-heading">Buttons</h2>
            <p>
              Zwei Button-Stile stehen zur Verfügung: ein gefüllter Primary-Button für die
              Hauptaktion und ein transparenter Outline-Button als sekundäre Aktion.
            </p>
          </div>

          <div className="sg-button-grid">
            <article className="sg-button-card">
              <div className="sg-button-preview">
                <span className="sg-button sg-button--primary">Mehr erfahren</span>
              </div>
              <dl className="sg-button-meta">
                <div>
                  <dt>Variante</dt>
                  <dd>Primary</dd>
                </div>
                <div>
                  <dt>Einsatz</dt>
                  <dd>Primäre Aktion pro Sektion, max. eine pro Ansicht.</dd>
                </div>
                <div>
                  <dt>Stil</dt>
                  <dd>
                    Fläche in <code>var(--st5-primary)</code>, Text in Weiß, Boston 600.
                  </dd>
                </div>
              </dl>
            </article>

            <article className="sg-button-card">
              <div className="sg-button-preview">
                <span className="sg-button sg-button--outline">Mehr erfahren</span>
              </div>
              <dl className="sg-button-meta">
                <div>
                  <dt>Variante</dt>
                  <dd>Outline</dd>
                </div>
                <div>
                  <dt>Einsatz</dt>
                  <dd>Sekundäre Aktion, häufig neben dem Primary-Button.</dd>
                </div>
                <div>
                  <dt>Stil</dt>
                  <dd>
                    Transparenter Hintergrund, 1,5&nbsp;px Border in{' '}
                    <code>var(--st5-primary)</code>, gleicher Textton.
                  </dd>
                </div>
              </dl>
            </article>
          </div>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-hero-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-hero-heading">ST5 Hero</h2>
            <p>
              So wird der ST5-Hero im Projekt aufgebaut: ein Bild als Hintergrund, darüber ein
              Overlay in der Hauptfarbe (Navy, 50&nbsp;% Deckkraft), weiße Typografie in Boston.
              Headline und Copy erhalten einen dezenten <code>text-shadow</code> als
              Kontrast-Puffer, damit sie auch auf helleren oder unruhigen Motiven lesbar bleiben.
            </p>
          </div>

          <HeroStage tagsAriaLabel="Schritte zum Aufbau der Hero-Section">
            <div className="sg-hero-demo" aria-label="Beispiel-Hero">
              <img
                className="sg-hero-demo-image"
                src={engineerImage}
                alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
              />
              <div className="sg-hero-demo-overlay" aria-hidden="true" />
              <div className="sg-hero-demo-logo" aria-hidden="true">
                <St5LogoWhite />
              </div>
              <svg
                className="sg-hero-demo-triangles"
                viewBox="0 0 260 260"
                preserveAspectRatio="xMaxYMax meet"
                aria-hidden="true"
              >
                <defs>
                  <filter
                    id="sg-triangle-shadow"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feDropShadow
                      dx="-3"
                      dy="-3"
                      stdDeviation="4"
                      floodColor="#000"
                      floodOpacity="0.25"
                    />
                    <feDropShadow
                      dx="-1"
                      dy="-1"
                      stdDeviation="0.5"
                      floodColor="#000"
                      floodOpacity="0.18"
                    />
                  </filter>
                </defs>
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--coral"
                  points="260,0 260,260 0,260"
                  fill="var(--st5-coral)"
                  filter="url(#sg-triangle-shadow)"
                />
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--purple"
                  points="260,60 260,260 60,260"
                  fill="var(--st5-purple)"
                  filter="url(#sg-triangle-shadow)"
                />
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--teal"
                  points="260,120 260,260 120,260"
                  fill="var(--st5-teal)"
                  filter="url(#sg-triangle-shadow)"
                />
              </svg>
              <div className="sg-hero-demo-content">
                <p className="sg-hero-demo-eyebrow">Smart Transform 5.0</p>
                <h3 className="sg-hero-demo-headline">
                  Co-creative Transformation in Richtung Industrie 5.0
                </h3>
                <p className="sg-hero-demo-copy">
                  Praxisnahe Use Cases, menschenzentrierte Technologie und Transfer zwischen
                  Wissenschaft und Wirtschaft — für KMU in Ostwestfalen-Lippe.
                </p>
                <div className="sg-hero-demo-actions">
                  <span className="sg-hero-demo-button sg-hero-demo-button--primary">
                    Mehr erfahren
                  </span>
                  <span className="sg-hero-demo-button sg-hero-demo-button--outline">
                    Zur Übersicht
                  </span>
                </div>
              </div>
            </div>
          </HeroStage>

          <dl className="sg-spec-list" aria-label="Hero-Spezifikation">
            <div>
              <dt>Bild</dt>
              <dd>
                Füllt die gesamte Fläche (<code>object-fit: cover</code>), ausreichend Höhe für
                lesbare Headline und Copy.
              </dd>
            </div>
            <div>
              <dt>Overlay</dt>
              <dd>
                <code>rgba(var(--st5-primary-rgb), 0.5)</code> — Navy mit 50&nbsp;%
                Deckkraft über dem Bild.
              </dd>
            </div>
            <div>
              <dt>Logo</dt>
              <dd>
                <code>st5_white.svg</code> oben links, auf gleicher X-Achse wie der Copy-Block.
              </dd>
            </div>
            <div>
              <dt>Typografie</dt>
              <dd>
                Headline in Boston 700, Copy in Boston 400, beides in Weiß; Eyebrow optional in
                uppercase.
              </dd>
            </div>
            <div>
              <dt>Text-Shadow</dt>
              <dd>
                Headline: <code>0 2px 10px rgba(0, 0, 0, 0.35)</code>, Copy:{' '}
                <code>0 1px 4px rgba(0, 0, 0, 0.25)</code> — zusätzlicher Kontrast-Puffer
                unabhängig vom Motiv.
              </dd>
            </div>
            <div>
              <dt>Dreifaltigkeit</dt>
              <dd>
                Drei rechtwinklige Dreiecke unten rechts in den Sub-Projekt-Farben{' '}
                <code>--st5-coral</code> (größte, hinten), <code>--st5-purple</code> (Mitte),{' '}
                <code>--st5-teal</code> (kleinste, vorne). Umgesetzt als inline-SVG mit{' '}
                <code>&lt;feDropShadow&gt;</code>-Filter, damit jedes Dreieck einen schwarzen
                Schatten auf das dahinterliegende wirft — sie wirken hierarchisch
                übereinandergelegt.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        className="sg-section"
        aria-labelledby="sg-hero-mensch-heading"
      >
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-hero-mensch-heading">ST5-Mensch Hero</h2>
            <p>
              Variante des ST5-Heros für das Sub-Projekt <strong>Mensch</strong>. Basis-Layout,
              Overlay (Navy) und Typografie bleiben bewusst unverändert — die Markenklammer ist
              also immer präsent. Die Zuordnung zum Sub-Projekt entsteht über zwei gezielte
              Akzente: die <strong>Dreifaltigkeit</strong> zeigt nur das Coral-Dreieck, und der
              Primary-Button greift Coral als Textfarbe auf.
            </p>
          </div>

          <HeroStage
            variant="mensch"
            tagsAriaLabel="Schritte zum Aufbau der Hero-Section (Mensch)"
          >
            <div className="sg-hero-demo" aria-label="Beispiel-Hero (Mensch)">
              <img
                className="sg-hero-demo-image"
                src={engineerImage}
                alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
              />
              <div className="sg-hero-demo-overlay" aria-hidden="true" />
              <div className="sg-hero-demo-logo" aria-hidden="true">
                <St5LogoWhite />
              </div>
              <svg
                className="sg-hero-demo-triangles"
                viewBox="0 0 260 260"
                preserveAspectRatio="xMaxYMax meet"
                aria-hidden="true"
              >
                <defs>
                  <filter
                    id="sg-triangle-shadow-mensch"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feDropShadow
                      dx="-3"
                      dy="-3"
                      stdDeviation="4"
                      floodColor="#000"
                      floodOpacity="0.25"
                    />
                    <feDropShadow
                      dx="-1"
                      dy="-1"
                      stdDeviation="0.5"
                      floodColor="#000"
                      floodOpacity="0.18"
                    />
                  </filter>
                </defs>
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--coral"
                  points="260,0 260,260 0,260"
                  fill="var(--st5-coral)"
                  filter="url(#sg-triangle-shadow-mensch)"
                />
                <g
                  className="sg-hero-demo-triangle-icon"
                  transform="translate(133 133)"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.534 16.827A24.5 24.5 0 0 1 37.502 10h.996a24.5 24.5 0 0 1 16.968 6.827l.363.347a23.3 23.3 0 0 1 6.196 10.154q.08.061.112.17l3.478 12.695c.697 2.546-1.219 5.057-3.858 5.057H59.18l-.696 8.704a6 6 0 0 1-6.524 5.497L47 59v9.25a2 2 0 0 1-2 2H23a2 2 0 0 1-2-2V51.61a25 25 0 0 1-.466-.437l-.363-.347a23.324 23.324 0 0 1 0-33.652z" />
                  <path d="m55 32.75l-2.07-.776c-.59-.221-.541-1.071.07-1.224l2-.5" />
                </g>
              </svg>
              <div className="sg-hero-demo-content">
                <p className="sg-hero-demo-eyebrow">Smart Transform 5.0 · Mensch</p>
                <h3 className="sg-hero-demo-headline">
                  Menschenzentrierte Technologie für Industrie 5.0
                </h3>
                <p className="sg-hero-demo-copy">
                  Wie KI, Assistenzsysteme und IoT den Arbeitsalltag konkret erleichtern — ohne
                  den Menschen aus dem Mittelpunkt zu drängen.
                </p>
                <div className="sg-hero-demo-actions">
                  <span className="sg-hero-demo-button sg-hero-demo-button--primary">
                    Mehr erfahren
                  </span>
                  <span className="sg-hero-demo-button sg-hero-demo-button--outline">
                    Zur Übersicht
                  </span>
                </div>
              </div>
            </div>
          </HeroStage>

          <dl className="sg-spec-list" aria-label="Hero-Spezifikation (Mensch)">
            <div>
              <dt>Basis bleibt Navy</dt>
              <dd>
                Overlay (<code>rgba(var(--st5-primary-rgb), 0.5)</code>) und alle UI-Elemente
                außerhalb des Sample-Bildes — insbesondere die Stepper-Tags — bleiben in der
                Hauptfarbe Navy. So bleibt die Markenklammer konsistent.
              </dd>
            </div>
            <div>
              <dt>Dreifaltigkeit</dt>
              <dd>
                In Sub-Projekt-Heros wird <strong>nur</strong> das Dreieck des jeweiligen
                Sub-Projekts gezeigt — hier also ausschließlich das Coral-Dreieck für
                <strong> Mensch</strong>. Zusätzlich sitzt im Zentrum des Dreiecks ein weißes
                Icon, das das Sub-Projekt symbolisiert (Mensch-Kopf).
              </dd>
            </div>
            <div>
              <dt>Primary-Button</dt>
              <dd>
                Invertiert: <code>background: var(--st5-coral)</code> mit weißer Schrift. Der
                einzige gezielte Farb-Akzent im Content-Bereich.
              </dd>
            </div>
            <div>
              <dt>Unverändert</dt>
              <dd>
                Logo, Typografie, Text-Shadow und Layout bleiben identisch zum Haupthero.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        className="sg-section"
        aria-labelledby="sg-hero-resilienz-heading"
      >
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-hero-resilienz-heading">ST5-Resilienz Hero</h2>
            <p>
              Variante des ST5-Heros für das Sub-Projekt <strong>Resilienz</strong>. Basis-Layout,
              Overlay (Navy) und Typografie bleiben unverändert. Die Zuordnung zum Sub-Projekt
              entsteht über zwei gezielte Akzente: die <strong>Dreifaltigkeit</strong> zeigt nur
              das Purple-Dreieck mit einem Schild-Icon als Symbol, und der Primary-Button greift
              Purple als Hintergrundfarbe auf.
            </p>
          </div>

          <HeroStage
            variant="resilienz"
            tagsAriaLabel="Schritte zum Aufbau der Hero-Section (Resilienz)"
          >
            <div className="sg-hero-demo" aria-label="Beispiel-Hero (Resilienz)">
              <img
                className="sg-hero-demo-image"
                src={engineerImage}
                alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
              />
              <div className="sg-hero-demo-overlay" aria-hidden="true" />
              <div className="sg-hero-demo-logo" aria-hidden="true">
                <St5LogoWhite />
              </div>
              <svg
                className="sg-hero-demo-triangles"
                viewBox="0 0 260 260"
                preserveAspectRatio="xMaxYMax meet"
                aria-hidden="true"
              >
                <defs>
                  <filter
                    id="sg-triangle-shadow-resilienz"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feDropShadow
                      dx="-3"
                      dy="-3"
                      stdDeviation="4"
                      floodColor="#000"
                      floodOpacity="0.25"
                    />
                    <feDropShadow
                      dx="-1"
                      dy="-1"
                      stdDeviation="0.5"
                      floodColor="#000"
                      floodOpacity="0.18"
                    />
                  </filter>
                </defs>
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--purple"
                  points="260,0 260,260 0,260"
                  fill="var(--st5-purple)"
                  filter="url(#sg-triangle-shadow-resilienz)"
                />
                <g
                  className="sg-hero-demo-triangle-icon"
                  transform="translate(133 133) scale(3.333)"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                </g>
              </svg>
              <div className="sg-hero-demo-content">
                <p className="sg-hero-demo-eyebrow">Smart Transform 5.0 · Resilienz</p>
                <h3 className="sg-hero-demo-headline">
                  Resiliente Wertschöpfung in volatilen Zeiten
                </h3>
                <p className="sg-hero-demo-copy">
                  Wie KMU ihre Lieferketten, Prozesse und Organisationen gegen Schocks absichern —
                  und aus Krisen gestärkt hervorgehen.
                </p>
                <div className="sg-hero-demo-actions">
                  <span className="sg-hero-demo-button sg-hero-demo-button--primary">
                    Mehr erfahren
                  </span>
                  <span className="sg-hero-demo-button sg-hero-demo-button--outline">
                    Zur Übersicht
                  </span>
                </div>
              </div>
            </div>
          </HeroStage>

          <dl className="sg-spec-list" aria-label="Hero-Spezifikation (Resilienz)">
            <div>
              <dt>Basis bleibt Navy</dt>
              <dd>
                Overlay (<code>rgba(var(--st5-primary-rgb), 0.5)</code>) und alle UI-Elemente
                außerhalb des Sample-Bildes — insbesondere die Stepper-Tags — bleiben in der
                Hauptfarbe Navy. So bleibt die Markenklammer konsistent.
              </dd>
            </div>
            <div>
              <dt>Dreifaltigkeit</dt>
              <dd>
                Es wird <strong>nur</strong> das Purple-Dreieck für
                <strong> Resilienz</strong> gezeigt. Im Zentrum sitzt ein weißes Schild-Icon als
                Symbol für Schutz und Widerstandskraft.
              </dd>
            </div>
            <div>
              <dt>Primary-Button</dt>
              <dd>
                Invertiert: <code>background: var(--st5-purple)</code> mit weißer Schrift. Der
                einzige gezielte Farb-Akzent im Content-Bereich.
              </dd>
            </div>
            <div>
              <dt>Unverändert</dt>
              <dd>
                Logo, Typografie, Text-Shadow und Layout bleiben identisch zum Haupthero.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        className="sg-section"
        aria-labelledby="sg-hero-nachhaltigkeit-heading"
      >
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-hero-nachhaltigkeit-heading">ST5-Nachhaltigkeit Hero</h2>
            <p>
              Variante des ST5-Heros für das Sub-Projekt <strong>Nachhaltigkeit</strong>.
              Basis-Layout, Overlay (Navy) und Typografie bleiben unverändert. Die Zuordnung zum
              Sub-Projekt entsteht über zwei gezielte Akzente: die <strong>Dreifaltigkeit</strong>{' '}
              zeigt nur das Teal-Dreieck mit einem Blatt-Icon als Symbol, und der Primary-Button
              greift Teal als Hintergrundfarbe auf.
            </p>
          </div>

          <HeroStage
            variant="nachhaltigkeit"
            tagsAriaLabel="Schritte zum Aufbau der Hero-Section (Nachhaltigkeit)"
          >
            <div className="sg-hero-demo" aria-label="Beispiel-Hero (Nachhaltigkeit)">
              <img
                className="sg-hero-demo-image"
                src={engineerImage}
                alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
              />
              <div className="sg-hero-demo-overlay" aria-hidden="true" />
              <div className="sg-hero-demo-logo" aria-hidden="true">
                <St5LogoWhite />
              </div>
              <svg
                className="sg-hero-demo-triangles"
                viewBox="0 0 260 260"
                preserveAspectRatio="xMaxYMax meet"
                aria-hidden="true"
              >
                <defs>
                  <filter
                    id="sg-triangle-shadow-nachhaltigkeit"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feDropShadow
                      dx="-3"
                      dy="-3"
                      stdDeviation="4"
                      floodColor="#000"
                      floodOpacity="0.25"
                    />
                    <feDropShadow
                      dx="-1"
                      dy="-1"
                      stdDeviation="0.5"
                      floodColor="#000"
                      floodOpacity="0.18"
                    />
                  </filter>
                </defs>
                <polygon
                  className="sg-hero-demo-triangle sg-hero-demo-triangle--teal"
                  points="260,0 260,260 0,260"
                  fill="var(--st5-teal)"
                  filter="url(#sg-triangle-shadow-nachhaltigkeit)"
                />
                <g
                  className="sg-hero-demo-triangle-icon"
                  transform="translate(133 133) scale(3.333)"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8c0 5.5-4.78 10-10 10" />
                  <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
                </g>
              </svg>
              <div className="sg-hero-demo-content">
                <p className="sg-hero-demo-eyebrow">Smart Transform 5.0 · Nachhaltigkeit</p>
                <h3 className="sg-hero-demo-headline">
                  Nachhaltige Industrie 5.0 für Ostwestfalen-Lippe
                </h3>
                <p className="sg-hero-demo-copy">
                  Wie KMU ökologisch, sozial und ökonomisch zukunftsfähig wirtschaften — mit
                  klarem Blick auf Ressourcen, Kreisläufe und Emissionen.
                </p>
                <div className="sg-hero-demo-actions">
                  <span className="sg-hero-demo-button sg-hero-demo-button--primary">
                    Mehr erfahren
                  </span>
                  <span className="sg-hero-demo-button sg-hero-demo-button--outline">
                    Zur Übersicht
                  </span>
                </div>
              </div>
            </div>
          </HeroStage>

          <dl className="sg-spec-list" aria-label="Hero-Spezifikation (Nachhaltigkeit)">
            <div>
              <dt>Basis bleibt Navy</dt>
              <dd>
                Overlay (<code>rgba(var(--st5-primary-rgb), 0.5)</code>) und alle UI-Elemente
                außerhalb des Sample-Bildes — insbesondere die Stepper-Tags — bleiben in der
                Hauptfarbe Navy. So bleibt die Markenklammer konsistent.
              </dd>
            </div>
            <div>
              <dt>Dreifaltigkeit</dt>
              <dd>
                Es wird <strong>nur</strong> das Teal-Dreieck für
                <strong> Nachhaltigkeit</strong> gezeigt. Im Zentrum sitzt ein weißes Blatt-Icon
                als Symbol für Natur, Kreisläufe und ökologische Verantwortung.
              </dd>
            </div>
            <div>
              <dt>Primary-Button</dt>
              <dd>
                Invertiert: <code>background: var(--st5-teal)</code> mit weißer Schrift. Der
                einzige gezielte Farb-Akzent im Content-Bereich.
              </dd>
            </div>
            <div>
              <dt>Unverändert</dt>
              <dd>
                Logo, Typografie, Text-Shadow und Layout bleiben identisch zum Haupthero.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="sg-section sg-section--alt" aria-labelledby="sg-imagery-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-imagery-heading">Bildbehandlung</h2>
            <p>
              Alle Projektbilder werden in die Markenklammer gesetzt — auch dann,
              wenn kein Text darauf steht. Bilder ohne Text erhalten ein Navy-Overlay
              mit <strong>30 % Deckkraft</strong>. So wirken Teaser-Kacheln,
              Presse-Artikel, Social-Posts und Blog-Headerbilder als Teil eines
              Systems statt als beliebige Stock-Fotos — analog zum 50 %-Overlay in
              den Heros, nur dezenter.
            </p>
          </div>

          <div className="sg-imagery-compare">
            <figure className="sg-imagery-card">
              <div className="sg-imagery-frame">
                <img
                  className="sg-imagery-image"
                  src={engineerImage}
                  alt="Ingenieurin an einer Anlage — Roh-Bild ohne Overlay"
                />
                <span className="sg-imagery-badge sg-imagery-badge--warn">
                  Nicht verwenden
                </span>
              </div>
              <figcaption>
                <span className="sg-imagery-label">Ohne Overlay</span>
                <span className="sg-imagery-sublabel">
                  Wirkt wie ein generisches Stock-Foto — kein Brand-Signal.
                </span>
              </figcaption>
            </figure>

            <figure className="sg-imagery-card">
              <div className="sg-imagery-frame">
                <img
                  className="sg-imagery-image"
                  src={engineerImage}
                  alt="Ingenieurin an einer Anlage — mit ST5-Bild-Overlay"
                />
                <div className="sg-imagery-overlay" aria-hidden="true" />
                <span className="sg-imagery-badge sg-imagery-badge--ok">
                  So verwenden
                </span>
              </div>
              <figcaption>
                <span className="sg-imagery-label sg-imagery-label--primary">
                  Mit ST5-Overlay
                </span>
                <span className="sg-imagery-sublabel">
                  Navy <code>#102F60</code> · Deckkraft <strong>30 %</strong> —
                  erkennbar als ST5-Bild.
                </span>
              </figcaption>
            </figure>
          </div>

          <dl className="sg-spec-list">
            <div>
              <dt>Farbe</dt>
              <dd>
                Navy <code>#102F60</code> — dieselbe Grundfarbe wie in den
                Hero-Overlays.
              </dd>
            </div>
            <div>
              <dt>Deckkraft</dt>
              <dd>
                <strong>30 %</strong> für Bilder ohne Text ·{' '}
                <strong>50 %</strong> für Heros mit Text darauf.
              </dd>
            </div>
            <div>
              <dt>Anwendung</dt>
              <dd>
                Teaser-Kacheln, Presse-Artikel-Header, Social-Posts, Blog-Hero-Bilder
                und sonstige dekorative Bilder auf Projekt-Oberflächen.
              </dd>
            </div>
            <div>
              <dt>Ausnahmen</dt>
              <dd>
                Logos, Partner-Marks, Portraits in der Team-Vorstellung, Grafiken und
                Diagramme bleiben unverändert.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-weblayouts-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-weblayouts-heading">Web-Sektionen</h2>
            <p>
              Wiederverwendbare Layout-Bausteine für Website-Seiten jenseits der
              Hero-Section. Alle Layouts nutzen dasselbe Design-System: Navy als
              Hauptfarbe, Boston als Schrift, die Sub-Farben ausschließlich für die
              Zuordnung zu den Sub-Projekten, die Dreifaltigkeit als wiedererkennbares
              Gestaltungselement.
            </p>
          </div>

          <div className="sg-weblayouts">
            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>Content + Visual</span>
                <span>2-spaltig · Text und Bild</span>
              </figcaption>
              <div className="sg-web-split">
                <div className="sg-web-split-text">
                  <p className="sg-web-eyebrow">Über das Projekt</p>
                  <h3 className="sg-web-headline">
                    Industrie 5.0 ist menschenzentriert, resilient und nachhaltig.
                  </h3>
                  <p className="sg-web-copy">
                    Smart Transform 5.0 begleitet KMU in Ostwestfalen-Lippe auf diesem
                    Weg — mit praxisnahen Use Cases, Transferformaten zwischen
                    Wissenschaft und Wirtschaft sowie passgenauer Qualifizierung für
                    das Team von morgen.
                  </p>
                  <a
                    className="sg-web-link"
                    href="#"
                    onClick={(e) => e.preventDefault()}
                  >
                    Mehr erfahren →
                  </a>
                </div>
                <figure className="sg-web-split-image">
                  <img
                    src={engineerImage}
                    alt="Beispielbild: Ingenieur:in im Lernlabor"
                  />
                  <span className="sg-web-split-image-overlay" aria-hidden="true" />
                </figure>
              </div>
              <p className="sg-weblayout-note">
                Für Fließtext-Abschnitte mit Visual. Text- und Bildseite pro
                Wiederholung abwechseln, um den Lesefluss lebendig zu halten. Bild
                immer mit <code>border-radius: 8px</code> und{' '}
                <strong>Navy-Overlay bei 30 % Deckkraft</strong> (siehe
                Bildbehandlung).
              </p>
            </figure>

            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>3-Spalten Sub-Projekt-Grid</span>
                <span>Mensch · Resilienz · Nachhaltigkeit</span>
              </figcaption>
              <div className="sg-web-features">
                <article className="sg-web-feature" data-variant="mensch">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 80 80"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20.534 16.827A24.5 24.5 0 0 1 37.502 10h.996a24.5 24.5 0 0 1 16.968 6.827l.363.347a23.3 23.3 0 0 1 6.196 10.154q.08.061.112.17l3.478 12.695c.697 2.546-1.219 5.057-3.858 5.057H59.18l-.696 8.704a6 6 0 0 1-6.524 5.497L47 59v9.25a2 2 0 0 1-2 2H23a2 2 0 0 1-2-2V51.61a25 25 0 0 1-.466-.437l-.363-.347a23.324 23.324 0 0 1 0-33.652z" />
                      <path d="m55 32.75l-2.07-.776c-.59-.221-.541-1.071.07-1.224l2-.5" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Mensch</h4>
                  <p className="sg-web-feature-copy">
                    Menschenzentrierte Technologie, die den Arbeitsalltag erleichtert
                    und individuelle Kompetenzen gezielt stärkt.
                  </p>
                </article>
                <article className="sg-web-feature" data-variant="resilienz">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Resilienz</h4>
                  <p className="sg-web-feature-copy">
                    Widerstandsfähige Wertschöpfung: Lieferketten, Prozesse und
                    Organisationen gegen Schocks absichern.
                  </p>
                </article>
                <article className="sg-web-feature" data-variant="nachhaltigkeit">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8c0 5.5-4.78 10-10 10" />
                      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Nachhaltigkeit</h4>
                  <p className="sg-web-feature-copy">
                    Ökologische, soziale und ökonomische Zukunftsfähigkeit mit Blick
                    auf Ressourcen, Kreisläufe und Emissionen.
                  </p>
                </article>
              </div>
              <p className="sg-weblayout-note">
                Ausschließlich für die drei Sub-Projekte. Reihenfolge immer{' '}
                <strong>Mensch → Resilienz → Nachhaltigkeit</strong>. Kein generisches
                Feature-Grid — dafür andere Layouts nutzen.
              </p>
            </figure>

            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>KPI-Row</span>
                <span>4-spaltig · Zahlen mit Beschriftung</span>
              </figcaption>
              <div className="sg-web-stats">
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">3</span>
                  <span className="sg-web-stat-label">Jahre Laufzeit</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">4</span>
                  <span className="sg-web-stat-label">Konsortialpartner</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">3</span>
                  <span className="sg-web-stat-label">Sub-Projekte</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">OWL</span>
                  <span className="sg-web-stat-label">Fokusregion</span>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Für Kennzahlen, Meilensteine, Reichweite. Zahl in Navy, Beschriftung in
                Grau darunter. Maximal vier Werte — sonst wirkt die Reihe visuell
                überladen.
              </p>
            </figure>

            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>CTA-Banner</span>
                <span>Full-bleed Navy · zentriert</span>
              </figcaption>
              <div className="sg-web-cta">
                <svg
                  className="sg-web-cta-triangles"
                  viewBox="0 0 200 200"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-web-cta-shadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-3"
                        dy="-3"
                        stdDeviation="4"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="0.5"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="200,0 200,200 0,200"
                    fill="var(--st5-coral)"
                    filter="url(#sg-web-cta-shadow)"
                  />
                  <polygon
                    points="200,50 200,200 50,200"
                    fill="var(--st5-purple)"
                    filter="url(#sg-web-cta-shadow)"
                  />
                  <polygon
                    points="200,100 200,200 100,200"
                    fill="var(--st5-teal)"
                    filter="url(#sg-web-cta-shadow)"
                  />
                </svg>
                <div className="sg-web-cta-content">
                  <p className="sg-web-cta-eyebrow">Jetzt mitmachen</p>
                  <h3 className="sg-web-cta-headline">
                    Bringen Sie Ihr Unternehmen auf den Weg zu Industrie 5.0.
                  </h3>
                  <p className="sg-web-cta-copy">
                    Kostenfreie Erstberatung, praxisnahe Use Cases, starke Partner aus
                    Wissenschaft und Wirtschaft.
                  </p>
                  <div className="sg-web-cta-actions">
                    <span className="sg-web-button sg-web-button--primary">
                      Kontakt aufnehmen
                    </span>
                    <span className="sg-web-button sg-web-button--outline">
                      Mehr erfahren
                    </span>
                  </div>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Abschluss-Baustein am Seitenende. Subtile Dreifaltigkeit rechts unten
                als Signature. Immer mit klarem, einzelnen CTA als Hauptaktion —
                Outline-Button nur als sekundäre Option.
              </p>
            </figure>
          </div>
        </div>
      </section>

      <section className="sg-section sg-section--alt" aria-labelledby="sg-slides-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-slides-heading">Präsentations-Folien</h2>
            <p>
              Folien-Vorlagen für Keynotes, Workshops und Konsortial-Präsentationen.
              Format <strong>16:9</strong>, Boston als Schrift, Navy als Grundton. Die
              Dreifaltigkeit kommt als Signature in Titel- und Abschluss-Folien zum
              Einsatz; in Kapitel-Folien wird nur das Dreieck des jeweiligen
              Sub-Projekts gezeigt — analog zu den Heros.
            </p>
          </div>

          <div className="sg-slides">
            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Titelfolie</span>
                <span>01</span>
              </figcaption>
              <div className="sg-slide sg-slide--title">
                <div className="sg-slide-logo">
                  <St5LogoWhite />
                </div>
                <p className="sg-slide-eyebrow">Smart Transform 5.0</p>
                <h3 className="sg-slide-title">
                  Co-creative Transformation in Richtung Industrie 5.0
                </h3>
                <p className="sg-slide-subtitle">
                  Konsortial-Kick-off · 27. März 2026 · InnovationSPIN Lemgo
                </p>
                <svg
                  className="sg-slide-triangles"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-slide-title-shadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-0.3"
                        dy="-0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-slide-title-shadow)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-slide-title-shadow)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-slide-title-shadow)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Einstieg jeder Präsentation. Logo oben links, Titel und Untertitel
                linksbündig, Dreifaltigkeit rechts unten.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Kapitel-Folie</span>
                <span>Mensch · Beispiel</span>
              </figcaption>
              <div
                className="sg-slide sg-slide--chapter"
                data-variant="mensch"
              >
                <div className="sg-slide-chapter-content">
                  <span className="sg-slide-chapter-number">01</span>
                  <h3 className="sg-slide-chapter-title">Mensch</h3>
                  <p className="sg-slide-chapter-copy">
                    Menschenzentrierte Technologie für Industrie 5.0.
                  </p>
                </div>
                <svg
                  className="sg-slide-chapter-triangle"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-slide-chapter-shadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-0.3"
                        dy="-0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-slide-chapter-shadow)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Trennfolie zu Beginn jedes Sub-Projekts. Für <strong>Resilienz</strong>{' '}
                statt Coral Purple und Nummer <code>02</code>, für{' '}
                <strong>Nachhaltigkeit</strong> Teal und <code>03</code>.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Inhaltsfolie</span>
                <span>Headline + Bullets</span>
              </figcaption>
              <div className="sg-slide sg-slide--content">
                <p className="sg-slide-eyebrow sg-slide-eyebrow--navy">
                  Use Case · Assistenzsysteme
                </p>
                <h3 className="sg-slide-content-title">
                  Drei Ansatzpunkte für menschenzentrierte KI.
                </h3>
                <ul className="sg-slide-bullets">
                  <li>
                    <span>01</span>
                    <span>
                      Wissensübergabe und Qualifizierung direkt am Arbeitsplatz.
                    </span>
                  </li>
                  <li>
                    <span>02</span>
                    <span>
                      Adaptive Assistenz für variantenreiche Fertigungsprozesse.
                    </span>
                  </li>
                  <li>
                    <span>03</span>
                    <span>Datensouveränität durch edge-nahe KI-Systeme.</span>
                  </li>
                </ul>
                <div className="sg-slide-footer">
                  <span>Smart Transform 5.0</span>
                  <span>03 / 12</span>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Standard-Layout für Inhalt. <strong>Maximal drei Bullets</strong> pro
                Folie — mehr nicht. Footer mit Projektname links und Seitenzahl
                rechts.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Agenda-Folie</span>
                <span>Outline / Inhaltsverzeichnis</span>
              </figcaption>
              <div className="sg-slide sg-slide--agenda">
                <div className="sg-slide-agenda-head">
                  <p className="sg-slide-eyebrow sg-slide-eyebrow--navy">
                    Workshop-Programm
                  </p>
                  <h3 className="sg-slide-agenda-title">Agenda</h3>
                </div>
                <ol className="sg-slide-agenda-list">
                  <li>
                    <span className="sg-slide-agenda-number">01</span>
                    <div>
                      <span className="sg-slide-agenda-item-title">
                        Projekt-Überblick
                      </span>
                      <span className="sg-slide-agenda-item-meta">10 min</span>
                    </div>
                  </li>
                  <li>
                    <span className="sg-slide-agenda-number">02</span>
                    <div>
                      <span className="sg-slide-agenda-item-title">
                        Sub-Projekte im Detail
                      </span>
                      <span className="sg-slide-agenda-item-meta">25 min</span>
                    </div>
                  </li>
                  <li>
                    <span className="sg-slide-agenda-number">03</span>
                    <div>
                      <span className="sg-slide-agenda-item-title">
                        Pilot-Use-Cases
                      </span>
                      <span className="sg-slide-agenda-item-meta">30 min</span>
                    </div>
                  </li>
                  <li>
                    <span className="sg-slide-agenda-number">04</span>
                    <div>
                      <span className="sg-slide-agenda-item-title">
                        Nächste Schritte
                      </span>
                      <span className="sg-slide-agenda-item-meta">15 min</span>
                    </div>
                  </li>
                </ol>
                <div className="sg-slide-footer">
                  <span>Smart Transform 5.0</span>
                  <span>02 / 12</span>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Zu Beginn jedes Termins. Maximal vier Punkte mit Zeitrichtwert.
                Reihenfolge bei Sub-Projekten immer{' '}
                <strong>Mensch → Resilienz → Nachhaltigkeit</strong>.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Zweispalter-Folie</span>
                <span>Text + Visual</span>
              </figcaption>
              <div className="sg-slide sg-slide--split">
                <div className="sg-slide-split-text">
                  <p className="sg-slide-eyebrow sg-slide-eyebrow--navy">
                    Transferformat
                  </p>
                  <h3 className="sg-slide-split-title">
                    Use-Case-Werkstatt für KMU in Ostwestfalen-Lippe.
                  </h3>
                  <p className="sg-slide-split-copy">
                    Co-kreative Arbeit an konkreten Anwendungsfällen — gemeinsam mit
                    Unternehmen, Forschung und Transferpartnern.
                  </p>
                  <ul className="sg-slide-split-meta">
                    <li>Ganztägiges Format</li>
                    <li>Max. 12 Teilnehmende</li>
                  </ul>
                  <div className="sg-slide-footer">
                    <span>Smart Transform 5.0</span>
                    <span>05 / 12</span>
                  </div>
                </div>
                <div
                  className="sg-slide-split-image"
                  style={{ backgroundImage: `url(${engineerImage})` }}
                  aria-hidden="true"
                />
              </div>
              <p className="sg-weblayout-note">
                Für Formate, die ein Bild brauchen — Use-Cases, Team-Vorstellungen,
                Formate mit Transferpartnern. Bild nimmt ca. 45 % der Breite ein.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Zitat-Folie</span>
                <span>Pull-Quote</span>
              </figcaption>
              <div className="sg-slide sg-slide--quote">
                <span className="sg-slide-quote-mark" aria-hidden="true">
                  „
                </span>
                <blockquote className="sg-slide-quote-text">
                  Industrie 5.0 heißt, Technologie wieder vom Menschen aus zu denken —
                  nicht umgekehrt.
                </blockquote>
                <footer className="sg-slide-quote-attr">
                  <span className="sg-slide-quote-author">Dr. Erika Mustermann</span>
                  <span className="sg-slide-quote-role">
                    Konsortialleitung · Hochschule Bielefeld
                  </span>
                </footer>
                <svg
                  className="sg-slide-triangles"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-slide-quote-shadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-0.3"
                        dy="-0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-slide-quote-shadow)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-slide-quote-shadow)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-slide-quote-shadow)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Für zentrale Aussagen, Stimmen aus der Region, Expert:innen-Insights.
                Maximal 25 Wörter — sonst wird es unlesbar. Immer mit voller Attribution
                (Name, Rolle, Organisation).
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Zeitstrahl-Folie</span>
                <span>Roadmap / Meilensteine</span>
              </figcaption>
              <div className="sg-slide sg-slide--roadmap">
                <p className="sg-slide-eyebrow sg-slide-eyebrow--navy">Projektverlauf</p>
                <h3 className="sg-slide-roadmap-title">
                  Drei Jahre Smart Transform 5.0.
                </h3>
                <ol className="sg-slide-roadmap-track">
                  <li>
                    <span className="sg-slide-roadmap-dot" aria-hidden="true" />
                    <span className="sg-slide-roadmap-date">03 · 2026</span>
                    <span className="sg-slide-roadmap-label">Kick-off</span>
                  </li>
                  <li>
                    <span className="sg-slide-roadmap-dot" aria-hidden="true" />
                    <span className="sg-slide-roadmap-date">Q2 · 2026</span>
                    <span className="sg-slide-roadmap-label">Pilot-Use-Cases</span>
                  </li>
                  <li>
                    <span className="sg-slide-roadmap-dot" aria-hidden="true" />
                    <span className="sg-slide-roadmap-date">2027 – 2028</span>
                    <span className="sg-slide-roadmap-label">
                      Transfer &amp; Skalierung
                    </span>
                  </li>
                  <li>
                    <span className="sg-slide-roadmap-dot" aria-hidden="true" />
                    <span className="sg-slide-roadmap-date">02 · 2029</span>
                    <span className="sg-slide-roadmap-label">Projekt-Abschluss</span>
                  </li>
                </ol>
                <div className="sg-slide-footer">
                  <span>Smart Transform 5.0</span>
                  <span>04 / 12</span>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Für Projektphasen, Arbeitspakete, Releases. Maximal vier Meilensteine pro
                Zeitstrahl. Dot-Farben folgen der Dreifaltigkeit — der letzte Meilenstein
                in Navy als Klammer.
              </p>
            </figure>

            <figure className="sg-slide-card">
              <figcaption className="sg-slide-label">
                <span>Abschluss-Folie</span>
                <span>Vielen Dank</span>
              </figcaption>
              <div className="sg-slide sg-slide--closing">
                <svg
                  className="sg-slide-triangles-tl"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMinYMin meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-slide-closing-shadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="1"
                        dy="1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="0.3"
                        dy="0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="0,0 100,0 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-slide-closing-shadow)"
                  />
                  <polygon
                    points="0,0 75,0 0,75"
                    fill="var(--st5-purple)"
                    filter="url(#sg-slide-closing-shadow)"
                  />
                  <polygon
                    points="0,0 50,0 0,50"
                    fill="var(--st5-teal)"
                    filter="url(#sg-slide-closing-shadow)"
                  />
                </svg>
                <div className="sg-slide-closing-content">
                  <div className="sg-slide-logo sg-slide-logo--closing">
                    <St5LogoWhite />
                  </div>
                  <h3 className="sg-slide-closing-title">Vielen Dank.</h3>
                  <p className="sg-slide-closing-copy">
                    kontakt@smart-transform-5.de · smart-transform-5.de
                  </p>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Für Q&amp;A und Kontakt-Abschluss. Spiegel der Titelfolie —
                Dreifaltigkeit diesmal oben links, Inhalt zentriert.
              </p>
            </figure>
          </div>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-socials-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-socials-heading">Social Media</h2>
            <p>
              Vorlagen für <strong>LinkedIn</strong> und <strong>Instagram</strong> — gleiche
              Markenklammer wie im Web und in Präsentationen: Navy-Grundton, Boston, das
              weiße Logo als Absender, Dreifaltigkeit als Signature. Bilder erhalten immer
              das Navy-Overlay (<code>50 %</code> mit Text, <code>30 %</code> ohne).
            </p>
          </div>

          <h3 className="sg-subheading">LinkedIn</h3>
          <div className="sg-socials sg-socials--linkedin">
            <figure className="sg-social-card">
              <figcaption className="sg-slide-label">
                <span>LinkedIn · Feed-Post</span>
                <span>1080 × 1080</span>
              </figcaption>
              <div className="sg-social sg-social--square sg-social--navy">
                <div className="sg-social-logo">
                  <St5LogoWhite />
                </div>
                <p className="sg-social-eyebrow">Save the Date</p>
                <h4 className="sg-social-headline">
                  Offizieller Start für Smart Transform 5.0
                </h4>
                <p className="sg-social-subtitle">
                  27. März 2026 · InnovationSPIN Lemgo
                </p>
                <svg
                  className="sg-social-triangles"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-social-shadow-li-square"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-0.3"
                        dy="-0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-social-shadow-li-square)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-social-shadow-li-square)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-social-shadow-li-square)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Quadratisches Feed-Format (1:1) für Ankündigungen, Meilensteine und
                Save-the-Date. Logo oben links, Dreifaltigkeit unten rechts.
              </p>
            </figure>

            <figure className="sg-social-card">
              <figcaption className="sg-slide-label">
                <span>LinkedIn · Link-Vorschau</span>
                <span>1200 × 627</span>
              </figcaption>
              <div className="sg-social sg-social--landscape sg-social--image">
                <img
                  className="sg-social-image"
                  src={engineerImage}
                  alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
                />
                <div className="sg-social-overlay" aria-hidden="true" />
                <div className="sg-social-logo">
                  <St5LogoWhite />
                </div>
                <div className="sg-social-content sg-social-content--bottom">
                  <p className="sg-social-eyebrow">Industrie 5.0 für OWL</p>
                  <h4 className="sg-social-headline">
                    Menschzentriert, resilient, nachhaltig — so wird Transformation konkret.
                  </h4>
                  <p className="sg-social-handle">smart-transform-5.de</p>
                </div>
                <svg
                  className="sg-social-triangles sg-social-triangles--small"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-social-shadow-li-landscape"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.3"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-social-shadow-li-landscape)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-social-shadow-li-landscape)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-social-shadow-li-landscape)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Landscape (1,91:1) für Link-Vorschauen und Artikel-Header. Bild mit{' '}
                <strong>50 % Navy-Overlay</strong>, Headline linksbündig unten, Logo oben links.
              </p>
            </figure>
          </div>

          <h3 className="sg-subheading sg-subheading--spaced">Instagram</h3>
          <div className="sg-socials sg-socials--instagram">
            <figure className="sg-social-card">
              <figcaption className="sg-slide-label">
                <span>Instagram · Feed-Post</span>
                <span>1080 × 1080</span>
              </figcaption>
              <div className="sg-social sg-social--square sg-social--quote">
                <span className="sg-social-quote-mark" aria-hidden="true">
                  „
                </span>
                <blockquote className="sg-social-quote">
                  Industrie 5.0 stellt den Menschen in den Mittelpunkt — nicht als
                  Optimierungs­objekt, sondern als Gestalter.
                </blockquote>
                <p className="sg-social-attribution">
                  Konsortial-Kickoff · 27. 03. 2026
                </p>
                <div className="sg-social-logo sg-social-logo--footer">
                  <St5LogoWhite />
                </div>
                <svg
                  className="sg-social-triangles"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-social-shadow-ig-square"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.25"
                      />
                      <feDropShadow
                        dx="-0.3"
                        dy="-0.3"
                        stdDeviation="0.2"
                        floodColor="#000"
                        floodOpacity="0.18"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-social-shadow-ig-square)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-social-shadow-ig-square)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-social-shadow-ig-square)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Quadratisches Feed-Format (1:1) für Zitate und Key-Messages. Typografie
                dominiert, Logo im Footer.
              </p>
            </figure>

            <figure className="sg-social-card sg-social-card--story">
              <figcaption className="sg-slide-label">
                <span>Instagram · Story</span>
                <span>1080 × 1920</span>
              </figcaption>
              <div className="sg-social sg-social--story sg-social--image">
                <img
                  className="sg-social-image"
                  src={engineerImage}
                  alt="Beispielbild: Ingenieur:in an einer Industrie-5.0-Anlage"
                />
                <div className="sg-social-overlay" aria-hidden="true" />
                <div className="sg-social-logo">
                  <St5LogoWhite />
                </div>
                <div className="sg-social-content sg-social-content--bottom">
                  <p className="sg-social-eyebrow">Save the Date</p>
                  <h4 className="sg-social-headline">
                    Offizieller Start Smart Transform 5.0
                  </h4>
                  <p className="sg-social-subtitle">
                    27. 03. 2026 · InnovationSPIN Lemgo
                  </p>
                  <span className="sg-social-cta">Mehr auf smart-transform-5.de →</span>
                </div>
                <svg
                  className="sg-social-triangles sg-social-triangles--story"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-social-shadow-ig-story"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1"
                        dy="-1"
                        stdDeviation="1.5"
                        floodColor="#000"
                        floodOpacity="0.3"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-social-shadow-ig-story)"
                  />
                  <polygon
                    points="100,25 100,100 25,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-social-shadow-ig-story)"
                  />
                  <polygon
                    points="100,50 100,100 50,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-social-shadow-ig-story)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Hochformat (9:16) für Teaser, Save-the-Date und Live-Eindrücke. Bild mit{' '}
                <strong>50 % Navy-Overlay</strong>, Logo oben links, CTA und Dreifaltigkeit
                unten.
              </p>
            </figure>
          </div>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-rollups-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-rollups-heading">Rollup-Banner</h2>
            <p>
              Klassischer Messestand-Rollup im Format <strong>100 × 200 cm</strong> (1:2). Der
              ST5-Rollup dient als Basis für Messen und Veranstaltungen mit Gesamt-Projektauftritt;
              je Sub-Projekt gibt es zusätzlich eine farblich fokussierte Variante. Produktion:
              300 dpi, CMYK, 3 mm Beschnitt, Dateiformat PDF/X oder Vektor-SVG.
            </p>
          </div>

          <div className="sg-rollups">
            <figure className="sg-rollup-card">
              <figcaption className="sg-slide-label">
                <span>ST5 · Haupt-Rollup</span>
                <span>100 × 200 cm</span>
              </figcaption>
              <div className="sg-rollup" data-variant="main" aria-label="Rollup Smart Transform 5.0">
                <div className="sg-rollup-inner">
                  <p className="sg-rollup-eyebrow">Smart Transform 5.0</p>
                  <div className="sg-rollup-logo">
                    <St5LogoWhite />
                  </div>
                  <div className="sg-rollup-content">
                    <h4 className="sg-rollup-headline">
                      Industrie 5.0 für Ostwestfalen-Lippe
                    </h4>
                    <p className="sg-rollup-copy">
                      Menschenzentriert. Resilient. Nachhaltig.
                    </p>
                  </div>
                  <div className="sg-rollup-footer">
                    <span className="sg-rollup-url">st-5.eu</span>
                  </div>
                </div>
                <svg
                  className="sg-rollup-triangles"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMax meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-rollup-shadow-main"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1.5"
                        dy="-1.5"
                        stdDeviation="2"
                        floodColor="#000"
                        floodOpacity="0.28"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="100,0 100,100 0,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-rollup-shadow-main)"
                  />
                  <polygon
                    points="100,30 100,100 30,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-rollup-shadow-main)"
                  />
                  <polygon
                    points="100,55 100,100 55,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-rollup-shadow-main)"
                  />
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Dachmarken-Rollup für Messen, Tagungen und Projektvorstellungen. Führt die
                vollständige <strong>Dreifaltigkeit</strong> in der unteren rechten Ecke — ohne
                Icon, als reines Farb-Statement der drei Sub-Projekte.
              </p>
            </figure>

            <figure className="sg-rollup-card">
              <figcaption className="sg-slide-label">
                <span>Rollup · Mensch</span>
                <span>100 × 200 cm</span>
              </figcaption>
              <div
                className="sg-rollup"
                data-variant="mensch"
                aria-label="Rollup Sub-Projekt Mensch"
              >
                <div className="sg-rollup-inner">
                  <p className="sg-rollup-eyebrow">Sub-Projekt · Mensch</p>
                  <div className="sg-rollup-logo">
                    <St5LogoWhite />
                  </div>
                  <div className="sg-rollup-content">
                    <h4 className="sg-rollup-headline">
                      Menschenzentrierte Technologie für Industrie 5.0
                    </h4>
                    <p className="sg-rollup-copy">
                      KI, Assistenzsysteme und IoT für den Arbeitsalltag von morgen.
                    </p>
                  </div>
                  <div className="sg-rollup-footer">
                    <span className="sg-rollup-url">st-5.eu</span>
                  </div>
                </div>
                <svg
                  className="sg-rollup-triangles sg-rollup-triangles--single"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMin meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-rollup-shadow-mensch"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1.5"
                        dy="1.5"
                        stdDeviation="2"
                        floodColor="#000"
                        floodOpacity="0.28"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="0,0 100,0 100,100"
                    fill="var(--st5-coral)"
                    filter="url(#sg-rollup-shadow-mensch)"
                  />
                  <g
                    transform="translate(52 19) scale(0.35)"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20.534 16.827A24.5 24.5 0 0 1 37.502 10h.996a24.5 24.5 0 0 1 16.968 6.827l.363.347a23.3 23.3 0 0 1 6.196 10.154q.08.061.112.17l3.478 12.695c.697 2.546-1.219 5.057-3.858 5.057H59.18l-.696 8.704a6 6 0 0 1-6.524 5.497L47 59v9.25a2 2 0 0 1-2 2H23a2 2 0 0 1-2-2V51.61a25 25 0 0 1-.466-.437l-.363-.347a23.324 23.324 0 0 1 0-33.652z" />
                  </g>
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Fokus-Rollup für Veranstaltungen zum Sub-Projekt <strong>Mensch</strong>. Nur das
                Coral-Dreieck mit Mensch-Icon; alle anderen Markenelemente bleiben unverändert.
              </p>
            </figure>

            <figure className="sg-rollup-card">
              <figcaption className="sg-slide-label">
                <span>Rollup · Resilienz</span>
                <span>100 × 200 cm</span>
              </figcaption>
              <div
                className="sg-rollup"
                data-variant="resilienz"
                aria-label="Rollup Sub-Projekt Resilienz"
              >
                <div className="sg-rollup-inner">
                  <p className="sg-rollup-eyebrow">Sub-Projekt · Resilienz</p>
                  <div className="sg-rollup-logo">
                    <St5LogoWhite />
                  </div>
                  <div className="sg-rollup-content">
                    <h4 className="sg-rollup-headline">
                      Resiliente Wertschöpfung in volatilen Zeiten
                    </h4>
                    <p className="sg-rollup-copy">
                      Lieferketten, Prozesse und Organisationen robust gestalten.
                    </p>
                  </div>
                  <div className="sg-rollup-footer">
                    <span className="sg-rollup-url">st-5.eu</span>
                  </div>
                </div>
                <svg
                  className="sg-rollup-triangles sg-rollup-triangles--single"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMin meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-rollup-shadow-resilienz"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1.5"
                        dy="1.5"
                        stdDeviation="2"
                        floodColor="#000"
                        floodOpacity="0.28"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="0,0 100,0 100,100"
                    fill="var(--st5-purple)"
                    filter="url(#sg-rollup-shadow-resilienz)"
                  />
                  <g
                    transform="translate(55 22) scale(1.05)"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                  </g>
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Fokus-Rollup für Veranstaltungen zum Sub-Projekt <strong>Resilienz</strong>. Nur
                das Purple-Dreieck mit Schild-Icon.
              </p>
            </figure>

            <figure className="sg-rollup-card">
              <figcaption className="sg-slide-label">
                <span>Rollup · Nachhaltigkeit</span>
                <span>100 × 200 cm</span>
              </figcaption>
              <div
                className="sg-rollup"
                data-variant="nachhaltigkeit"
                aria-label="Rollup Sub-Projekt Nachhaltigkeit"
              >
                <div className="sg-rollup-inner">
                  <p className="sg-rollup-eyebrow">Sub-Projekt · Nachhaltigkeit</p>
                  <div className="sg-rollup-logo">
                    <St5LogoWhite />
                  </div>
                  <div className="sg-rollup-content">
                    <h4 className="sg-rollup-headline">
                      Nachhaltige Industrie 5.0 für OWL
                    </h4>
                    <p className="sg-rollup-copy">
                      Ressourcen, Kreisläufe, Emissionen — konkret angehen.
                    </p>
                  </div>
                  <div className="sg-rollup-footer">
                    <span className="sg-rollup-url">st-5.eu</span>
                  </div>
                </div>
                <svg
                  className="sg-rollup-triangles sg-rollup-triangles--single"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMaxYMin meet"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="sg-rollup-shadow-nachhaltigkeit"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="-1.5"
                        dy="1.5"
                        stdDeviation="2"
                        floodColor="#000"
                        floodOpacity="0.28"
                      />
                    </filter>
                  </defs>
                  <polygon
                    points="0,0 100,0 100,100"
                    fill="var(--st5-teal)"
                    filter="url(#sg-rollup-shadow-nachhaltigkeit)"
                  />
                  <g
                    transform="translate(55 22) scale(1.05)"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8c0 5.5-4.78 10-10 10" />
                    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
                  </g>
                </svg>
              </div>
              <p className="sg-weblayout-note">
                Fokus-Rollup für Veranstaltungen zum Sub-Projekt{' '}
                <strong>Nachhaltigkeit</strong>. Nur das Teal-Dreieck mit Blatt-Icon.
              </p>
            </figure>
          </div>

          <dl className="sg-spec-list" aria-label="Rollup-Spezifikation">
            <div>
              <dt>Format</dt>
              <dd>
                100 × 200 cm, Hochformat (1:2). Sichtbarer Bereich beachten: die obersten und
                untersten ~10 cm werden am Stand teils verdeckt oder geknickt.
              </dd>
            </div>
            <div>
              <dt>Hintergrund</dt>
              <dd>
                Vollflächig Navy (<code>#102F60</code>), keine Bilder im Hintergrund. Optionen mit
                Bild sind als separate Varianten zu behandeln und brauchen{' '}
                <strong>50 % Navy-Overlay</strong> (siehe Bildbehandlung).
              </dd>
            </div>
            <div>
              <dt>Layout</dt>
              <dd>
                Logo oben, Headline-Block mittig, URL unten — plus Dreifaltigkeit in der unteren
                rechten Ecke als wiedererkennbares Marken-Element.
              </dd>
            </div>
            <div>
              <dt>Produktion</dt>
              <dd>
                Druckdaten als PDF/X-4 oder Vektor-SVG anliefern. Auflösung 300 dpi (falls Bilder
                enthalten), 3 mm Beschnitt rundum, CMYK-Farbraum. Schriften eingebettet oder in
                Pfade konvertiert.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="sg-section sg-section--alt" aria-labelledby="sg-partners-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-partners-heading">Partnerlogos</h2>
            <p>
              Logos der geförderten Projektpartner. Immer auf weißem Hintergrund verwenden,
              Seitenverhältnis nicht verändern.
            </p>
          </div>

          <div className="sg-partner-grid">
            {PARTNERS.map((p) => (
              <div key={p.file} className="sg-partner-card">
                <div className="sg-partner-logo">
                  <img src={p.file} alt={`Logo ${p.name}`} loading="lazy" />
                </div>
                <div className="sg-partner-meta">
                  <span className="sg-partner-name">{p.name}</span>
                  <span className="sg-partner-role">{p.role}</span>
                  <a href={p.file} download className="sg-logo-download">
                    SVG herunterladen
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sg-section" aria-labelledby="sg-funding-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-funding-heading">Fördermittelgeber</h2>
            <p>
              Das Projekt wird gefördert durch das EFRE/JTF-Programm NRW 2021–2027 (Regio.NRW –
              Transformation). Das Logo ist auf allen Projekt-Outputs verpflichtend sichtbar zu
              platzieren.
            </p>
          </div>

          <div className="sg-partner-card sg-partner-card--wide">
            <div className="sg-partner-logo">
              <img src="/logos/efre.svg" alt="EFRE.NRW Logo" loading="lazy" />
            </div>
            <div className="sg-partner-meta">
              <span className="sg-partner-name">EFRE.NRW</span>
              <span className="sg-partner-role">Förderprogramm</span>
              <a href="/logos/efre.svg" download className="sg-logo-download">
                SVG herunterladen
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="sg-footer">
        <div className="sg-section-inner">
          <p>
            Referenz-Dokumentation: <code>docs/STYLE_GUIDE.md</code> · CSS-Variablen:{' '}
            <code>frontend/src/index.css</code>.
          </p>
          <p className="sg-footer-note">Nur für internen Gebrauch im ST5 Team.</p>
        </div>
      </footer>
    </div>
  );
}

function Styleguide() {
  useEffect(() => {
    document.title = 'Styleguide · Smart Transform 5.0';
    return () => {
      document.title = 'Smart Transform 5.0 - Industrie 5.0 Forschungsprojekt';
    };
  }, []);

  return (
    <StyleguideGate>
      <StyleguideContent />
    </StyleguideGate>
  );
}

export default Styleguide;
